import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { currentBuildVersion, fetchServedBuildVersion } from '@/app/buildVersion';
import { useIsWaiterSurface } from './waiterSurface';

// Même cadence que le sondage natif de SvelteKit sur les deux tableaux de bord
// (`version: { pollInterval: 60_000 }`). Le carnet reste ouvert des heures :
// une minute d'écart n'a jamais coûté quoi que ce soit, une requête toutes les
// 10 s sur le chemin critique du service, si.
const VERSION_POLL_MS = 60_000;

/**
 * SC-763 — le bandeau « votre terminal tourne sur une ancienne version ».
 *
 * Pourquoi ici et pas partout : le carnet du serveur est l'écran qui reste
 * ouvert le plus longtemps de toute la plateforme — un terminal déverrouillé au
 * début du service et gardé en main pendant des heures. Un onglet client, lui,
 * est une visite ponctuelle qui meurt toute seule, et poser un bandeau devant un
 * client en cours de paiement serait une décision produit, pas un effet de bord.
 * Le composant est monté au-dessus de TOUTES les pages du restaurant
 * (App.tsx / RestaurantRoutes) mais `useIsWaiterSurface` le rend inerte hors de
 * la grille des tables et des tables ouvertes depuis elle — exactement la porte
 * qui gouverne déjà WaiterPendingOrdersHost.
 *
 * Échoue ouvert, dans les trois sens : pas d'empreinte locale (dev, où le
 * serveur Vite recharge déjà tout seul), pas de réponse lisible du point de
 * version, ou empreinte servie vide ⇒ aucun bandeau. Rien n'est jamais bloqué.
 */
export default function StaleBuildBanner() {
  const { restaurantUuid = '' } = useParams<{ restaurantUuid: string }>();
  const { t } = useTranslation();
  const isWaiterSurface = useIsWaiterSurface(restaurantUuid);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const current = currentBuildVersion();
    // `stale` fait partie des dépendances : une fois le bandeau levé, cet effet
    // rejoue, sort ici, et le sondage s'arrête. Il n'y a plus rien à apprendre.
    if (!isWaiterSurface || !current || stale) return;

    let cancelled = false;
    const controller = new AbortController();

    const check = async () => {
      if (document.hidden) return;
      const served = await fetchServedBuildVersion(controller.signal);
      if (!cancelled && served && served !== current) setStale(true);
    };
    const runCheck = () => {
      void check();
    };

    runCheck();
    const timer = setInterval(runCheck, VERSION_POLL_MS);
    // Un terminal posé sur la table revient souvent après une longue pause :
    // le retour à l'écran est le meilleur moment pour reposer la question.
    document.addEventListener('visibilitychange', runCheck);

    return () => {
      cancelled = true;
      controller.abort();
      clearInterval(timer);
      document.removeEventListener('visibilitychange', runCheck);
    };
  }, [isWaiterSurface, stale]);

  if (!stale) return null;

  return (
    <div
      data-testid="stale-build-banner"
      role="alert"
      className="fixed inset-x-0 top-0 z-[700] flex items-center justify-between gap-3 bg-red-600 px-3 py-2 font-display text-sm text-white"
    >
      <span>{t('waiter.staleBuild.message')}</span>
      <button
        type="button"
        data-testid="stale-build-reload"
        onClick={() => window.location.reload()}
        className="shrink-0 rounded-btn-sm border-2 border-white px-3 py-1 font-display text-sm"
      >
        {t('waiter.staleBuild.reload')}
      </button>
    </div>
  );
}
